import React from "react";
import {
	DragDropContext,
	Draggable,
	type DraggableChildrenFn,
	type DraggableProvided,
	Droppable,
	type DropResult,
} from "react-beautiful-dnd";

import {
	faBullseyeArrow,
	faCog,
	faCopy,
	faExternalLink as faExternalLinkLight,
	faEyeSlash,
	faFileAlt,
	faLink,
	faMapMarkerCheck,
	faPen,
	faTrashCan,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCheck, faEllipsisV, faExternalLink, faThumbsUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import copy from "copy-to-clipboard";
import { commitMutation, graphql } from "relay-runtime";

import { ReactComponent as DraggableGrip } from "~/legacy-assets/images/icons/draggable-grip.svg";
import { ReactComponent as MenuNotesNavigation } from "~/legacy-assets/images/icons/menu-notes-navigation.svg";
import { ReactComponent as FellowLogo } from "~/legacy-assets/images/logos/fellow-icon-mono.svg";
import { useFeature, WithFeature } from "~/lib/flippy";
import { useMedia } from "~/lib/hooks";
import { environment } from "~/models";
import type { StreamSettingsResponseStream, StreamSettingsViewer } from "~/stream/StreamSettings";
import { getLocalTabId } from "~/stream/utils";
import { Button, toast } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { useAddStreamTabModal } from "../../AddStreamTabModal";
import { useBookmarkTabModal } from "../../BookmarkTabModal";
import type { StreamTab, TabType } from "../../StreamHeader/TabsAndBookmarks";

import type { AncestryRelationship } from "~/__generated__/StreamSettingsModalQuery.graphql";
import type { TabsSettingsAddGoalsetTabMutation } from "~/__generated__/TabsSettingsAddGoalsetTabMutation.graphql";
import type { TabsSettingsRemoveTabMutation } from "~/__generated__/TabsSettingsRemoveTabMutation.graphql";
import type { TabsSettingsReorderTabMutation } from "~/__generated__/TabsSettingsReorderTabMutation.graphql";
import type { TabsSettingsToggleFellowTabMutation } from "~/__generated__/TabsSettingsToggleFellowTabMutation.graphql";

import styles from "./TabsSettings.module.css";

const toggleFellowTabMutation = graphql`
	mutation TabsSettingsToggleFellowTabMutation($enable: Boolean!, $tabId: String!, $streamId: ID!) {
		StreamTab_ToggleFellowTab(enable: $enable, tabId: $tabId, streamId: $streamId) {
			stream {
				id
				isMeetingFeedbackTabEnabled
				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

const removeTabMutation = graphql`
	mutation TabsSettingsRemoveTabMutation($streamId: ID!, $tabId: String!) {
		StreamTab_RemoveTab(streamId: $streamId, tabId: $tabId) {
			stream {
				id
				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

const reorderTabMutation = graphql`
	mutation TabsSettingsReorderTabMutation($streamId: ID!, $tabId: String!, $newIndex: Int!) @raw_response_type {
		StreamTab_ReorderTab(streamId: $streamId, tabId: $tabId, newIndex: $newIndex) {
			stream {
				id
				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

const addGoalsetTabMutation = graphql`
	mutation TabsSettingsAddGoalsetTabMutation($streamId: ID!) {
		StreamTab_AddGoalsetTab(streamId: $streamId) {
			stream {
				id
				tabs {
					...TabsAndBookmarks_noteStreamTab @relay(mask: false)
				}
			}
		}
	}
`;

export const toggleFellowTab = (streamId: string, tabId: TabType) => (checked: boolean) => {
	if (tabId === "notes") return;

	commitMutation<TabsSettingsToggleFellowTabMutation>(environment, {
		mutation: toggleFellowTabMutation,
		variables: { enable: checked, tabId, streamId },
		onCompleted: (response, error) => {
			if (error) {
				Sentry.captureException(new Error("Error when toggling Fellow tab"), {
					extra: { relayError: error },
				});
				toast.error("Sorry! Something went wrong while enabling that tab");
			} else if (!response.StreamTab_ToggleFellowTab?.stream) {
				Sentry.captureException(new Error("Bad response when toggling Fellow tab "), {
					extra: { relayResponse: response },
				});
				toast.error("Sorry! Something went wrong while enabling that tab");
			}
		},
	});
};

export const addGoalsetTab = (streamId: string) => () => {
	commitMutation<TabsSettingsAddGoalsetTabMutation>(environment, {
		mutation: addGoalsetTabMutation,
		variables: {
			streamId: streamId,
		},
		onCompleted: (response, errors) => {
			if (errors) {
				toast.error("Something went wrong! Please try again later, or contact support.");
			}
		},
	});
};

interface TabProps {
	streamId: string;
	tab: StreamTab;
	goToConfigure?: () => void;
	dndProvided: DraggableProvided;
	rel: AncestryRelationship | "";
}

function DefaultTabComponent({ tab }: Pick<TabProps, "tab">) {
	const noCursor = useMedia(`(pointer: coarse)`);
	return (
		<div className={cx(styles.tab, { [styles.noDragHandle]: !noCursor })}>
			<div className={styles.tabIcon}>
				<MenuNotesNavigation width={16} height={16} className={styles.genericIcon} />
			</div>
			<div className={styles.tabText}>
				<span className={cx(styles.tabTextTitle, styles.tabTextTitleCapped)}>{tab.title}</span>
				<div>
					<span>(default)</span>
				</div>
			</div>
		</div>
	);
}

function TabComponent({ streamId, tab, goToConfigure, dndProvided, rel }: TabProps) {
	const openEditBookmarkModal = useBookmarkTabModal(streamId, tab);
	const noCursor = useMedia(`(pointer: coarse)`);
	const hideFellowTab = React.useCallback(
		(tabId: "action-items" | "feedback" | "objectives") => () => toggleFellowTab(streamId, tabId)(false),
		[streamId],
	);
	const [tabIcon, setTabIcon] = React.useState(tab.icon);

	const onImageError = React.useCallback(() => {
		setTabIcon(null);
	}, []);

	const TabIcon = () => {
		switch (getLocalTabId(tab.id)) {
			case "action-items":
				return <FontAwesomeIcon icon={faCheck} className={styles.genericIcon} />;
			case "feedback":
				return <FontAwesomeIcon icon={faThumbsUp} className={styles.genericIcon} />;
			case "objectives":
				return <FontAwesomeIcon icon={faBullseyeArrow} className={styles.genericIcon} />;
			default:
				return tab.otherStreamId ? (
					<FellowLogo width={16} height={16} className={styles.fellowIcon} />
				) : tabIcon ? (
					<img src={tabIcon} alt="Tab icon" width={16} height={16} onError={onImageError} />
				) : (
					<FontAwesomeIcon icon={faLink} className={styles.genericIcon} />
				);
		}
	};

	const copyLink = React.useCallback(() => {
		if (!tab.url) return;
		copy(tab.url);
		toast.info(`Copied to clipboard!`, { autoClose: 5000 });
	}, [tab.url]);

	const removeTab = React.useCallback(() => {
		commitMutation<TabsSettingsRemoveTabMutation>(environment, {
			mutation: removeTabMutation,
			variables: {
				streamId: streamId,
				tabId: tab.id,
			},
			onCompleted: (response, errors) => {
				if (errors) {
					toast.error("We had a problem removing that tab, try again");
					throw new Error((errors || []).map(e => e.message).join(";"));
				}
			},
		});
	}, [streamId, tab.id]);

	const menuItems: JSX.Element[] = [];
	const tabId = getLocalTabId(tab.id);
	const is1on1Feedback = tabId === "feedback" && (rel === "DIRECT_REPORT" || rel === "MANAGER");
	let tabTitle = tab.title;
	if (tabId === "feedback") {
		if (rel === "DIRECT_REPORT") {
			tabTitle = "Feedback about my report";
		} else if (rel === "MANAGER") {
			tabTitle = "Feedback about me";
		}
	}

	if (tabId === "action-items" || tabId === "feedback" || tabId === "objectives") {
		if (tabId !== "action-items" && !is1on1Feedback) {
			menuItems.push(
				<Menu.Item key="configure" icon={faCog} onSelect={goToConfigure}>
					Configure
				</Menu.Item>,
			);
		}

		menuItems.push(
			<Menu.Item key="hide" icon={faEyeSlash} onSelect={hideFellowTab(tabId)}>
				Hide tab
			</Menu.Item>,
		);
	} else {
		if (tab.url !== null) {
			menuItems.push(
				<Menu.Item key="edit-bookmark" icon={faPen} onSelect={openEditBookmarkModal}>
					Edit bookmark
				</Menu.Item>,
				<Menu.ItemLink
					key="open-link"
					icon={faExternalLinkLight}
					href={tab.url}
					target="_blank"
					rel="noopener noreferrer"
				>
					Open in new tab
				</Menu.ItemLink>,
				<Menu.Item key="copy-link" icon={faCopy} onSelect={copyLink}>
					Copy link
				</Menu.Item>,
			);
		}

		if (menuItems.length > 1) menuItems.push(<Menu.Separator key="delete-separator" />);
		menuItems.push(
			<Menu.Item key="delete" icon={faTrashCan} destructive onSelect={removeTab}>
				Delete
			</Menu.Item>,
		);
	}

	return (
		<div className={styles.tab} ref={dndProvided.innerRef} {...dndProvided.draggableProps}>
			{!noCursor && (
				<div {...dndProvided.dragHandleProps} className={styles.dragHandle}>
					<DraggableGrip fill="var(--tertiaryTextColor)" width={8} height={12} />
				</div>
			)}
			<div className={styles.tabIcon}>
				<TabIcon />
			</div>
			<div className={styles.tabText}>
				<span className={cx(styles.tabTextTitle, { [styles.tabTextTitleCapped]: tab.url != null })}>{tabTitle}</span>
				{tab.url !== null && (
					<div>
						<span>{tab.url}</span>
					</div>
				)}
				{tab.url !== null && !tab.isEmbedded && (
					<span>
						<FontAwesomeIcon icon={faExternalLink} />
					</span>
				)}
			</div>
			<Menu mobileHeaderTitle={tabTitle}>
				<Menu.Trigger>
					<IconButton icon={faEllipsisV} title="Configure this tab" className={styles.tripeDotMenu} />
				</Menu.Trigger>
				<Menu.Content data-ignore-focus-lock>{menuItems}</Menu.Content>
			</Menu>
		</div>
	);
}

function getDraggableTabContent(
	tabs: StreamTab[],
	goToSection: (section: "meeting-feedback" | "objectives") => void,
	stream: {
		id: string;
		displayName: string;
	},
	rel: AncestryRelationship | "",
): DraggableChildrenFn {
	return function DraggableGroupClone(provided, snapshot, rubric) {
		const goTo = (section: "meeting-feedback" | "objectives") => () => goToSection(section);
		const tab = tabs[rubric.source.index];
		const tabId = getLocalTabId(tab.id);

		return (
			<TabComponent
				dndProvided={provided}
				streamId={stream.id}
				tab={tab}
				rel={rel}
				goToConfigure={
					tabId === "feedback" ? goTo("meeting-feedback") : tabId === "objectives" ? goTo("objectives") : undefined
				}
			/>
		);
	};
}

interface TabsSettingsProps {
	stream: StreamSettingsResponseStream;
	viewer: StreamSettingsViewer;
	goToSection: (section: "meeting-feedback" | "objectives") => void;
}

export default function TabsSettings({ stream, viewer, goToSection }: TabsSettingsProps) {
	const openAddTabNotesModal = useAddStreamTabModal(stream.id, stream.displayName, viewer);
	const openAddBookmarkModal = useBookmarkTabModal(stream.id);
	const okrsDisabled = useFeature("okrs-disabled", "account") || !viewer.account.billingAccount.hasAccessToObjectives;
	const rel = stream.otherUser?.relationshipToViewer ?? "";

	const sortedTabs = Array.from(stream.tabs).sort((tabA, tabB) => tabA.order - tabB.order);
	const [defaultTabs, draggableTabs]: StreamTab[][] = sortedTabs.reduce(
		(result, tab) => {
			// NOTE: The order keys of the tabs must be consecutive for react-beautiful-dnd.
			// When using a feature flag on a tab (i.e. objectives in this case) the tab data may still contain the tab.
			// When it's then hidden, the order keys will no longer be consecutive.
			// Therefore it's reasonable to reassign order keys with knowledge of which tabs will be hidden

			if (getLocalTabId(tab.id) === "notes") result[0].push({ ...tab, order: -1 });
			// Hide objectives tab if okrs is disabled
			else if (!okrsDisabled || getLocalTabId(tab.id) !== "objectives") {
				result[1].push({ ...tab, order: result[1].length });
			}
			return result;
		},
		[[], []] as StreamTab[][],
	);

	const tabRenderer = React.useMemo(
		() => getDraggableTabContent(draggableTabs, goToSection, stream, rel),
		[draggableTabs, goToSection, rel, stream],
	);

	const onDragEnd = React.useCallback(
		({ destination, draggableId, source }: DropResult) => {
			if (!destination || destination.index === source.index) return;

			let newIndex = destination.index + 1; // +1 is because we ignore the notes tab when reassigning order keys

			// Making up for a potentially hidden objectives tab
			if (okrsDisabled) {
				const objectivesTabOrderId = stream.tabs.find(t => t.id === "objectives")?.order;
				if (objectivesTabOrderId && destination.index >= objectivesTabOrderId) {
					newIndex += 1;
				}
			}

			const oldIndex = stream.tabs.findIndex(t => t.id === draggableId);
			const tabs = Array.from(stream.tabs).map((tab, i) => {
				// Change the order of the tab that's been dragged
				if (i === oldIndex) return { ...tab, order: newIndex };

				// If the dragged tab used to be before this tab, decrement this tab's order
				if (oldIndex < i && i <= newIndex) return { ...tab, order: i - 1 };

				// If the dragged tab used to be after this tab, increment this tab's order
				if (newIndex <= i && i < oldIndex) return { ...tab, order: i + 1 };

				return tab;
			});

			commitMutation<TabsSettingsReorderTabMutation>(environment, {
				mutation: reorderTabMutation,
				variables: {
					streamId: stream.id,
					tabId: draggableId,
					newIndex: newIndex,
				},
				optimisticResponse: { StreamTab_ReorderTab: { stream: { id: stream.id, tabs } } }, // eslint-disable-line
			});
		},
		[okrsDisabled, stream],
	);

	const priorityLabel = window.APP_CONFIG?.priorityLabel || "Priority";
	const prioritiesLabel = window.APP_CONFIG?.prioritiesLabel || "Priorities";

	return (
		<main>
			<header className={styles.header}>
				<h1>Tabs and bookmarks</h1>
				<Menu mobileHeaderTitle="Options">
					<Menu.Trigger>
						<Button variant="primary" size="normal">
							Add
						</Button>
					</Menu.Trigger>
					<Menu.Content data-ignore-focus-lock>
						<Menu.Item icon={faLink} onSelect={openAddBookmarkModal}>
							Add a bookmark
							<br />
							<small>Easily access important links</small>
						</Menu.Item>
						<Menu.Item icon={faFileAlt} onSelect={openAddTabNotesModal}>
							Add Fellow notes as a tab
							<br />
							<small>Link notes from another series</small>
						</Menu.Item>
						<Menu.Separator />
						{rel == "DIRECT_REPORT" && !stream.tabs.find(tab => tab.title.includes(`${priorityLabel}:`)) && (
							<WithFeature id="okrs-disabled" subject="account">
								<>
									<Menu.Item icon={faMapMarkerCheck} onSelect={addGoalsetTab(stream.id)}>
										Add {prioritiesLabel} as a tab
										<br />
										<small>Use another series to track goals</small>
									</Menu.Item>
									<Menu.Separator />
								</>
							</WithFeature>
						)}
						<Menu.SwitchItem
							icon={faCheck}
							checked={Boolean(stream.tabs.find(tab => getLocalTabId(tab.id) === "action-items"))}
							onCheckedChange={toggleFellowTab(stream.id, "action-items")}
						>
							Action items
						</Menu.SwitchItem>
						{!okrsDisabled && (
							<Menu.SwitchItem
								icon={faBullseyeArrow}
								checked={Boolean(stream.tabs.find(tab => getLocalTabId(tab.id) === "objectives"))}
								onCheckedChange={toggleFellowTab(stream.id, "objectives")}
							>
								Objectives
							</Menu.SwitchItem>
						)}
						<Menu.SwitchItem
							icon={faThumbsUp}
							checked={Boolean(stream.tabs.find(tab => getLocalTabId(tab.id) === "feedback"))}
							onCheckedChange={toggleFellowTab(stream.id, "feedback")}
						>
							{rel === "DIRECT_REPORT"
								? "Feedback about my report"
								: rel === "MANAGER"
									? "Feedback about me"
									: stream.type === "meeting"
										? "Meeting feedback"
										: "Feedback"}
						</Menu.SwitchItem>
					</Menu.Content>
				</Menu>
			</header>
			<div>
				<div className={styles.tabs}>
					{defaultTabs.map(tab => (
						<DefaultTabComponent key={tab.id} tab={tab} />
					))}
				</div>
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="orderedTabs" renderClone={tabRenderer}>
						{provided => (
							<div {...provided.droppableProps} ref={provided.innerRef} className={styles.tabs}>
								{draggableTabs.map(tab => (
									<Draggable key={tab.id} draggableId={tab.id} index={tab.order}>
										{tabRenderer}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</div>
		</main>
	);
}
